import { ClientOnly } from "@tanstack/react-router";
import clsx from "clsx";
import { lazy, Suspense } from "react";

import AgentCompatibility from "../components/AgentCompatibility";
import { WIP_ENABLED_TRUSTED_BY } from "../components/constants";
import FallbackBackground from "../components/FallbackBackground";
import Features from "../components/Features";
import FinalCTAMobile from "../components/FinalCTAMobile";
import Footer from "../components/Footer";
import GradientSection from "../components/GradientSection";
import Header from "../components/Header";
import LandingFAQ from "../components/LandingFAQ";
import LandingHero from "../components/LandingHero";
import PurchaseFinalCTA from "../components/PurchaseFinalCTA";
import Testimonials from "../components/Testimonials";
import TrustedBy from "../components/TrustedBy";

// The static FallbackBackground below is always rendered behind the canvas, so
// if a deploy purged this chunk we degrade to nothing here rather than letting
// the rejected import bubble to the route error boundary and crash the page.
const ScrollReactiveBackground = lazy(() =>
  import("../components/ScrollReactiveBackground").catch(() => ({
    default: () => <></>,
  })),
);

// Hairline separator between sections of the black chapter.
function SectionDivider({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={clsx("h-px w-full bg-white/[0.08]", className)}
    />
  );
}

export default function Landing() {
  return (
    <div
      className="bg-background text-foreground dark relative min-h-screen overflow-x-clip"
      style={{ scrollBehavior: "smooth" }}
      id="top"
    >
      {/* Static gradient — paints immediately on SSR, then the WebGL canvas
          layers on top once its chunk has loaded. */}
      <FallbackBackground variant="dark" />

      {/* Shader-based Background System — client-only, lazy-loaded */}
      <ClientOnly>
        <Suspense fallback={null}>
          <ScrollReactiveBackground variant="dark" />
        </Suspense>
      </ClientOnly>

      {/* Content with relative positioning to appear above background */}
      <div className="relative z-10 pb-16 lg:pb-0">
        <Header />
        <main>
          <LandingHero />

          {/* Black chapter — the waves cut out below the hero and return
              for the final CTA. */}
          <div className="relative">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-[#05060a]"
              style={{
                maskImage:
                  "linear-gradient(to bottom, transparent, black 8rem, black calc(100% - 10rem), transparent)",
                WebkitMaskImage:
                  "linear-gradient(to bottom, transparent, black 8rem, black calc(100% - 10rem), transparent)",
              }}
            />
            <AgentCompatibility />
            {WIP_ENABLED_TRUSTED_BY && <TrustedBy />}
            {/* GradientSection is desktop-only, so its leading divider is
                too — otherwise mobile shows a doubled line. */}
            <SectionDivider className="hidden lg:block" />
            <GradientSection />
            <SectionDivider />
            <Features />
            <SectionDivider />
            <Testimonials />
            <SectionDivider />
            <LandingFAQ />
          </div>

          <PurchaseFinalCTA />
        </main>
        <Footer />
      </div>

      <FinalCTAMobile />
    </div>
  );
}
